import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { browserHistory } from 'browserHistory';

import { useFetchOrdersDeliveredAtForReport } from 'hooks/useFetchOrdersDeliveredAtForReport';
import { useFetchShiftByRegion } from 'hooks/useFetchShift';

import { BillingCourier } from 'components/OrderListTable/BillingCourier/BillingCourier';
import { Loader } from 'components/Loader/Loader';

import {
  CalendarFetchByPeriods,
  getEndDate,
  getStartDate,
} from 'components/OrderListTable/CalendarFetch/CalendarFetchByPeriods';
import { ReqEnum } from 'const/requestStatus';

import './MyDeliveriesReportComposition.scss';
import { CourierInvoice } from '../../types/reportTypes';

import dayjs from 'dayjs';
import { ShiftCourierWithDateJson } from '../../const/ShiftConst';
import {
  ShiftInvoiceDataToReplace,
  errorCourierInvoiceObjReplacment,
  replaceDataOfCourierInvoiceIfMissingCompanyData,
} from './InvoiceMultipleCourierReportComposition';
import { useFetchCourierById } from '../../hooks/useFetchCouriers';
import { flatten } from 'utils/array';
import analyticsWrapper from '../../utils/analyticsWrapper';
import { Region } from '../../types/orderTypes';
import { Link } from 'react-router-dom';

function createMyInvoiceData(
  courierInvoiceList: CourierInvoice[],
  courierListBookedFetched: ShiftCourierWithDateJson[]
): CourierInvoice {
  const myInvoice: CourierInvoice = courierInvoiceList[0];
  if (myInvoice === undefined && courierListBookedFetched.length) {
    const courierData: {
      name: string;
      courierId: number;
    } = flatten(
      courierListBookedFetched.map(obj =>
        obj.courierDayBooked.map(obj1 => {
          const returnObj = { name: obj1.name, courierId: obj1.courierId };
          return returnObj;
        })
      )
    )[0];

    const courierInvoiceObjReplacment: CourierInvoice = {
      counts: [],
      courierCompanyName: ShiftInvoiceDataToReplace.courierCompanyName,
      courierCompanyNumber: ShiftInvoiceDataToReplace.courierCompanyNumber,
      courierFirstName: courierData ? courierData.name : '',
      courierId: courierData ? courierData.courierId : 0,
      courierLastName: '',
      courierTravelMode: ShiftInvoiceDataToReplace.courierTravelMode,
      courierAvgRatingForDateRange:
        ShiftInvoiceDataToReplace.courierAvgRatingForDateRange,
    };

    return courierInvoiceObjReplacment;
  } else if (
    myInvoice === undefined &&
    courierListBookedFetched === undefined
  ) {
    return errorCourierInvoiceObjReplacment;
  } else {
    return myInvoice;
  }
}

export function MyDeliveriesReportComposition({
  region,
}: {
  region: Region;
}): JSX.Element {
  const [startDate, setStartDate] = useState<dayjs.Dayjs>(getStartDate());
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(getEndDate());

  const fetchValues = values => {
    const { fetchStartDate, fetchEndDate } = values;
    setStartDate(fetchStartDate);
    setEndDate(fetchEndDate);
  };

  const [courierInvoiceList, fetchStatus] = useFetchOrdersDeliveredAtForReport(
    startDate,
    endDate
  );

  const [courierListBookedFetched, fetchStatusShift] = useFetchShiftByRegion(
    startDate,
    endDate,
    region
  );

  const myInvoice: CourierInvoice = createMyInvoiceData(
    courierInvoiceList,
    courierListBookedFetched
  );

  const [courierData, fetchCoruierDataStatus] = useFetchCourierById(
    myInvoice === undefined
      ? undefined
      : myInvoice.courierId
      ? myInvoice.courierId.toString()
      : undefined
  );

  const UpdatedMyInvoice: CourierInvoice =
    replaceDataOfCourierInvoiceIfMissingCompanyData(
      myInvoice,
      fetchCoruierDataStatus,
      courierData
    );
  return (
    <>
      <Helmet>
        <title>{'My Deliveries (old)'}</title>
      </Helmet>
      <div className="u-screen-only">
        <CalendarFetchByPeriods
          fetchValues={fetchValues}
          givenStartDate={startDate}
          givenEndDate={endDate}
        />
        {fetchStatus === ReqEnum.PENDING ||
          (fetchStatusShift === ReqEnum.PENDING && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Loader type="spinner" size="large" />
            </div>
          ))}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            className="btn btn-primary"
            id="How-to-send-invoice-from-my-deliveries"
            style={{ padding: '5px' }}
            onClick={() => {
              analyticsWrapper.event({
                category: 'courier-invoice',
                action: `How-to-send-invoice-from-my-deliveries button click`,
                label: 'click',
              });
              browserHistory.push('my-tips');
            }}
          >
            How to send invoice?
          </button>
        </div>
      </div>

      {endDate.isBefore(new Date('2021-06-01')) ? (
        <BillingCourier
          courierInvoiceList={UpdatedMyInvoice}
          fetchStatus={fetchStatus}
          startDate={startDate}
          endDate={endDate}
          courierListBookedFetched={courierListBookedFetched}
          fetchStatusShift={fetchStatusShift}
          region={region}
        />
      ) : (
        <p style={{ textAlign: 'center', marginTop: '30vh', fontSize: '20px' }}>
          This report is only available for periods before June 1st, 2021.
          Please change period accordingly to see older data, or use the{' '}
          <Link to="/my-deliveries">new invoice page</Link>.
        </p>
      )}
    </>
  );
}
